import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, ArrowLeft, Save } from 'lucide-react';
import { toast } from 'sonner';
import { services } from '@/pages/services/serviceData';

interface Contact { id: string; name: string | null; email: string; }
interface Project {
  id: string; contact_id: string; service_slug: string; status: string; notes: string | null;
  created_at: string; contacts?: Contact;
}
interface ProjectCheck {
  id: string; project_id: string; checklist_item_id: string; is_completed: boolean;
  completed_at: string | null; notes: string | null;
  service_checklist_items?: { title: string; description: string; phase: string; position: number; };
}

const STATUS_COLORS: Record<string, string> = {
  discovery: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-amber-100 text-amber-800',
  delivered: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-600',
};

const ProjectsPage = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [checks, setChecks] = useState<ProjectCheck[]>([]);
  const [checksLoading, setChecksLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newContactId, setNewContactId] = useState('');
  const [newService, setNewService] = useState(services[0].slug);
  const [newNotes, setNewNotes] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterService, setFilterService] = useState('all');
  const [projectStatus, setProjectStatus] = useState('discovery');
  const [projectNotes, setProjectNotes] = useState('');

  const fetchProjects = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('client_projects')
      .select('*, contacts(id, name, email)')
      .order('created_at', { ascending: false });
    if (error) toast.error(error.message);
    else setProjects((data as any[]) || []);
    setLoading(false);
  };

  const fetchContacts = async () => {
    const { data } = await supabase.from('contacts').select('id, name, email').order('name');
    setContacts((data as Contact[]) || []);
  };

  useEffect(() => { fetchProjects(); fetchContacts(); }, []);

  const fetchChecks = async (projectId: string) => {
    setChecksLoading(true);
    const { data, error } = await supabase
      .from('client_project_checks')
      .select('*, service_checklist_items(title, description, phase, position)')
      .eq('project_id', projectId)
      .order('created_at');
    if (error) toast.error(error.message);
    else setChecks((data as any[]) || []);
    setChecksLoading(false);
  };

  const openProject = (project: Project) => {
    setSelectedProject(project);
    setProjectStatus(project.status);
    setProjectNotes(project.notes || '');
    fetchChecks(project.id);
  };

  const handleCreate = async () => {
    if (!newContactId) { toast.error('Select a contact'); return; }
    // Create project
    const { data: proj, error: projErr } = await supabase.from('client_projects').insert({
      contact_id: newContactId, service_slug: newService, notes: newNotes,
    }).select().single();
    if (projErr || !proj) { toast.error(projErr?.message || 'Failed'); return; }

    // Copy template items into project checks
    const { data: templateItems } = await supabase.from('service_checklist_items')
      .select('id').eq('service_slug', newService);
    if (templateItems && templateItems.length > 0) {
      const checksToInsert = templateItems.map((t: any) => ({
        project_id: proj.id, checklist_item_id: t.id,
      }));
      await supabase.from('client_project_checks').insert(checksToInsert);
    }
    toast.success('Project created');
    setDialogOpen(false);
    setNewContactId(''); setNewNotes('');
    fetchProjects();
  };

  const toggleCheck = async (check: ProjectCheck) => {
    const newVal = !check.is_completed;
    const { error } = await supabase.from('client_project_checks').update({
      is_completed: newVal,
      completed_at: newVal ? new Date().toISOString() : null,
    }).eq('id', check.id);
    if (error) toast.error(error.message);
    else setChecks(prev => prev.map(c => c.id === check.id ? { ...c, is_completed: newVal, completed_at: newVal ? new Date().toISOString() : null } : c));
  };

  const saveCheckNotes = async (checkId: string, notes: string) => {
    await supabase.from('client_project_checks').update({ notes }).eq('id', checkId);
  };

  const saveProjectDetails = async () => {
    if (!selectedProject) return;
    const { error } = await supabase.from('client_projects').update({
      status: projectStatus, notes: projectNotes,
    }).eq('id', selectedProject.id);
    if (error) toast.error(error.message);
    else { toast.success('Saved'); fetchProjects(); }
  };


  const serviceName = (slug: string) => services.find(s => s.slug === slug)?.title || slug;

  const filtered = projects.filter(p => {
    if (filterStatus !== 'all' && p.status !== filterStatus) return false;
    if (filterService !== 'all' && p.service_slug !== filterService) return false;
    return true;
  });

  // Detail view
  if (selectedProject) {
    const scopingChecks = checks.filter(c => c.service_checklist_items?.phase === 'scoping')
      .sort((a, b) => (a.service_checklist_items?.position || 0) - (b.service_checklist_items?.position || 0));
    const implChecks = checks.filter(c => c.service_checklist_items?.phase === 'implementation')
      .sort((a, b) => (a.service_checklist_items?.position || 0) - (b.service_checklist_items?.position || 0));
    const completed = checks.filter(c => c.is_completed).length;
    const total = checks.length;
    const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

    const renderCheckSection = (sectionChecks: ProjectCheck[], label: string) => (
      <div className="space-y-2">
        <h3 className="text-base font-semibold" style={{ color: 'var(--ink)' }}>{label}</h3>
        {sectionChecks.map(check => (
          <Card key={check.id} className="p-3 space-y-2">
            <div className="flex items-start gap-3">
              <Checkbox
                checked={check.is_completed}
                onCheckedChange={() => toggleCheck(check)}
                className="mt-0.5"
              />
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${check.is_completed ? 'line-through opacity-60' : ''}`} style={{ color: 'var(--ink)' }}>
                  {check.service_checklist_items?.title}
                </p>
                {check.service_checklist_items?.description && (
                  <p className="text-xs mt-0.5" style={{ color: 'var(--slate)' }}>{check.service_checklist_items.description}</p>
                )}
                {check.completed_at && (
                  <p className="text-xs mt-1" style={{ color: 'var(--green, #16a34a)' }}>
                    ✓ Completed {new Date(check.completed_at).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
            <Textarea
              placeholder="Notes…"
              defaultValue={check.notes || ''}
              onBlur={e => saveCheckNotes(check.id, e.target.value)}
              className="text-xs"
              rows={1}
            />
          </Card>
        ))}
      </div>
    );

    return (
      <div className="space-y-6 max-w-4xl">
        <Button variant="ghost" onClick={() => setSelectedProject(null)} className="mb-2">
          <ArrowLeft className="w-4 h-4 mr-1" />Back to Projects
        </Button>

        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-fraunces font-bold" style={{ color: 'var(--ink)' }}>
              {(selectedProject as any).contacts?.name || (selectedProject as any).contacts?.email}
            </h1>
            <p className="text-sm" style={{ color: 'var(--slate)' }}>{serviceName(selectedProject.service_slug)}</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={projectStatus} onValueChange={setProjectStatus}>
              <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="discovery">Discovery</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" onClick={saveProjectDetails}><Save className="w-3 h-3 mr-1" />Save</Button>
          </div>
        </div>

        <div className="space-y-1">
          <div className="flex justify-between text-sm" style={{ color: 'var(--slate)' }}>
            <span>Progress</span>
            <span>{completed}/{total} ({pct}%)</span>
          </div>
          <Progress value={pct} className="h-2" />
        </div>

        <Textarea
          placeholder="Project notes…"
          value={projectNotes}
          onChange={e => setProjectNotes(e.target.value)}
          rows={2}
        />

        {checksLoading ? (
          <div className="animate-pulse py-8 text-center" style={{ color: 'var(--slate)' }}>Loading…</div>
        ) : (
          <div className="grid gap-8 md:grid-cols-2">
            {renderCheckSection(scopingChecks, '🔍 Scoping Questions')}
            {renderCheckSection(implChecks, '🚀 Implementation Steps')}
          </div>
        )}
      </div>
    );
  }

  // List view
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="text-2xl font-fraunces font-bold" style={{ color: 'var(--ink)' }}>Client Projects</h1>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button><Plus className="w-4 h-4 mr-1" />New Project</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create Client Project</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-2">
              <div>
                <label className="text-sm font-medium" style={{ color: 'var(--ink)' }}>Contact</label>
                <Select value={newContactId} onValueChange={setNewContactId}>
                  <SelectTrigger><SelectValue placeholder="Select contact…" /></SelectTrigger>
                  <SelectContent>
                    {contacts.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name || c.email} ({c.email})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium" style={{ color: 'var(--ink)' }}>Service</label>
                <Select value={newService} onValueChange={setNewService}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {services.map(s => (
                      <SelectItem key={s.slug} value={s.slug}>{s.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium" style={{ color: 'var(--ink)' }}>Notes</label>
                <Textarea value={newNotes} onChange={e => setNewNotes(e.target.value)} placeholder="Initial notes…" rows={2} />
              </div>
              <Button onClick={handleCreate} className="w-full">Create Project</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex gap-3 flex-wrap">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="discovery">Discovery</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterService} onValueChange={setFilterService}>
          <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Services</SelectItem>
            {services.map(s => (
              <SelectItem key={s.slug} value={s.slug}>{s.title}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="animate-pulse py-8 text-center" style={{ color: 'var(--slate)' }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <Card className="p-8 text-center" style={{ color: 'var(--slate)' }}>No projects found. Create one to get started.</Card>
      ) : (
        <div className="rounded-lg border" style={{ borderColor: 'var(--line)' }}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Contact</TableHead>
                <TableHead>Service</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(p => (
                <TableRow key={p.id}>
                  <TableCell className="font-medium">{(p as any).contacts?.name || (p as any).contacts?.email || '—'}</TableCell>
                  <TableCell className="text-sm">{serviceName(p.service_slug)}</TableCell>
                  <TableCell>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[p.status] || ''}`}>
                      {p.status.replace('_', ' ')}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm" style={{ color: 'var(--slate)' }}>
                    {new Date(p.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Button size="sm" variant="ghost" onClick={() => openProject(p)}>Open</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default ProjectsPage;
